import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../Redux/store';
import { fetchFiles, fetchFileData, setCurrentFileIndex } from '../../../Redux/fileSlice';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Box, Paper, TableCell } from '@mui/material';
import RoutineStyle from "../../../components/GlobalPolicies/RoutineUpload.module.css";
import Slide from '@mui/material/Slide';
import { TransitionProps } from "@mui/material/transitions";
import UserDetailsTable from '../utils/UserDetailsTable';
import ProjectManagementFileTabs from '../utils/ProjectManagementFileTabs';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

interface ProjectManagementFileViewProps {
  open: boolean;
  onClose: () => void;
  name: string;
  id: any;
  functionl1?: string;
  current_project?: string;
  date?: any;
  updatedBy?:any;
  sectionTitle?: string;
  userDetail?: boolean;
  apiPath?: string;
  getFileImagePath?: (doc_path: string) => string;
}

const Transition = React.forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  (props, ref) => {
    return <Slide direction="left" ref={ref} {...props} />;
  }
);

export default function ProjectManagementFileView({
  open,
  onClose,
  name,
  id,
  functionl1,
  current_project,
  date,
  updatedBy,
  sectionTitle,
  userDetail,
  apiPath,
  getFileImagePath,
}: ProjectManagementFileViewProps) {
  const dispatch: AppDispatch = useDispatch();
  const { files, currentFileIndex, loading, error } = useSelector((state: RootState) => state.files);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const hasFetchedFiles = useRef(false); // Add a ref to track if files have been fetched

  console.log('updatedBy', updatedBy)

  useEffect(() => {
    console.log('useEffect triggered'); // Debug log
  
    if (open && apiPath && !hasFetchedFiles.current) {
      console.log('Fetching files...'); // Debug log
      dispatch(fetchFiles(apiPath))
        .unwrap()
        .then((files) => {
          console.log('Files fetched:', files); // Debug log
          if (files.length > 0) {
            dispatch(fetchFileData({ docPath: files[0].doc_path, index: 0 }));
            dispatch(setCurrentFileIndex(0));
          }
          hasFetchedFiles.current = true;
        })
        .catch((err) => {
          console.error('Failed to fetch files:', err);
        });
    }
  
    return () => {
      if (!open) {
        console.log('Resetting hasFetchedFiles'); // Debug log
        hasFetchedFiles.current = false;
      }
    };
  }, [open, apiPath, dispatch]);

  const handleTabChange = (index: number) => {
    const selectedFile = files[index];
    if (!selectedFile.fileData) {
      dispatch(fetchFileData({ docPath: selectedFile.doc_path, index }));
    }
    dispatch(setCurrentFileIndex(index));
  };

  const getFileType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) {
      return 'image';
    }
    return 'unknown';
  };

  const currentFile = files[currentFileIndex];

  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      TransitionComponent={Transition}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%', maxWidth: 800, position: "absolute",
          top: "0%",
          right: "0%",
          bottom: "0%",
          height: '100%', margin: 1,
        }
      }}
    >
      <Box>
      <Typography variant="h6" sx={{ m: 0, p: 2, color: '#000' }} gutterBottom>
        {sectionTitle}
      </Typography>
      <DialogTitle sx={{ m: 0, p: 2, color: '#000' }} id="customized-dialog-title">
        {name.split("_").slice(8).join("_")}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>

      <ProjectManagementFileTabs
        open={open}
        imageSrc={imageSrc}
        apiPath={apiPath}
        getFileType={getFileType}
      />

      {userDetail && updatedBy && (
        <UserDetailsTable date={date} updatedBy={updatedBy} functionl1={functionl1} current_project={current_project}  />
      )}

      {/* file section */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          p: 2,
        }}
      >
        {files.map(
          (
            file: { doc_path: string; file_name: string; file_size: any },
            index: React.Key | null | undefined
          ) => (
            <Paper
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                p: 1.5,
                bgcolor: currentFileIndex === index ? "grey.300" : "transparent",
                border: currentFileIndex === index ? "1px solid grey.500" : "none",
                borderRadius: 1.5,
                width: 200,
                transition: "background-color 0.3s ease",
              }}
              onClick={() => typeof index === "number" && handleTabChange(index)}
            >
              <Box component="img"
                src={getFileImagePath ? getFileImagePath(file.doc_path) : ""}
                alt="File"
                sx={{ height: 50, width: 50 }}
              />
              <Box sx={{ display: "grid", gap: 1 }}>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '12px' }}>
                  {file.file_name.split("_").slice(8).join("_")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  {file.file_size || "Unknown Size"}
                </Typography>
              </Box>
            </Paper>
          )
        )}
      </Box>

      <Box
        bottom={0}
        padding={2}
        width="100%"
        sx={{
          boxShadow: '-1px -1px 13.2px 0px rgba(0, 0, 0, 0.1)',
          bgcolor: '#fff',
          zIndex: 10,
        }}>
        <Button variant="outlined" sx={{ color: '#000', borderColor: '#000' }} onClick={onClose}>
          Cancel
        </Button>
      </Box>
      </Box>
    </BootstrapDialog>
  );
}